import React, { useState } from "react";
import {
	Paper,
	Grid,
	TextField,
	Typography,
	useTheme,
	Snackbar,
	Button,
	Alert,
	CircularProgress,
} from "@mui/material";
import {
	useGoogleReCaptcha,
	recaptchaSubmit,
	sendContactForm,
} from "gatsby-frontend";

const GATSBY_BACKEND_URL = process.env.GATSBY_BACKEND_URL;

const defaultOptions = {
	title: "Contact us",
	messageLabel: "Message",
	hiddenInputs: ["last_name"],
};

export default function ContactForm({ options = {}, onSubmitForm }) {
	const theme = useTheme();

	const { executeRecaptcha } = useGoogleReCaptcha();

	const [contactFormValues, setContactFormValues] = useState({});
	const [formErrors, setFormErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [snackMessage, setSnackMessage] = useState({ open: false });

	const subumitForm = async (e) => {
		e.preventDefault();

		if (!isValidForm()) {
			return;
		}

		setSubmitting(true);

		const isValidRecaptcha = await recaptchaSubmit(
			executeRecaptcha,
			GATSBY_BACKEND_URL
		);

		if (!isValidRecaptcha) {
			setSubmitting(false);
			setSnackMessage({
				open: true,
				severity: "warning",
				message: "Please verify that you are not a robot.",
			});
			return;
		}

		const exec = onSubmitForm || sendContactForm;
		let response = await exec({ data: contactFormValues }, GATSBY_BACKEND_URL);

		if (response == true) {
			setSnackMessage({
				open: true,
				severity: "success",
				message: "Contact message sent!",
			});
			setContactFormValues({});
		} else {
			setSnackMessage({
				open: true,
				severity: "error",
				message: "There was an error sending the contact message",
			});
		}

		setSubmitting(false);
	};

	const onFormInputChange = (e) => {
		e.preventDefault();
		contactFormValues[e.target.name] = e.target.value;
		setContactFormValues({ ...contactFormValues });
	};

	const handleSnackClose = () => {
		setSnackMessage({
			...snackMessage,
			open: false,
		});
	};

	const isValidForm = () => {
		let errors = {};

		var validEmailRegex =
			/^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

		inputs.forEach((input) => {
			if (
				!hiddenInputs.includes(input.name) &&
				!contactFormValues[input.name]
			) {
				errors[input.name] = "This field is required";
			}
		});

		if (
			!hiddenInputs.includes("email") &&
			!contactFormValues["email"]?.match(validEmailRegex)
		) {
			errors["email"] = "Enter a valid email";
		}

		setFormErrors(errors);

		if (Object.keys(errors).length > 0) {
			return false;
		}

		return true;
	};

	const {
		title = defaultOptions.title,
		hiddenInputs = defaultOptions.hiddenInputs,
	} = options;

	const inputs = getInputs(options);
	const hiddenFilters = (input) => !hiddenInputs.includes(input.name);

	return (
		<>
			<Paper
				component="form"
				elevation={3}
				sx={{
					padding: theme.spacing(3, 2),
				}}
				onSubmit={subumitForm}
			>
				<Typography
					variant="h4"
					sx={(theme) => ({ marginBottom: theme.spacing(2) })}
				>
					{title}
				</Typography>
				<Grid container spacing={2}>
					{inputs.filter(hiddenFilters).map((input) => (
						<Grid item xs={12} key={input.name}>
							<TextField
								{...input}
								value={contactFormValues[input.name] || ""}
								onChange={onFormInputChange}
								error={formErrors[input.name]}
								helperText={formErrors[input.name]}
							/>
						</Grid>
					))}
					<Grid item xs={12}>
						<Button
							disabled={submitting}
							type="submit"
							fullWidth
							variant="contained"
						>
							{!submitting ? "Submit" : <CircularProgress size={20} />}
						</Button>
					</Grid>
				</Grid>
			</Paper>
			<Snackbar
				open={snackMessage.open}
				autoHideDuration={6000}
				onClose={handleSnackClose}
			>
				<Alert
					onClose={handleSnackClose}
					severity={snackMessage.severity || "success"}
					sx={{ width: "100%" }}
				>
					{snackMessage.message}
				</Alert>
			</Snackbar>
		</>
	);
}

function getInputs(options) {
	return [
		{
			label: "Name",
			variant: "outlined",
			fullWidth: true,
			name: "name",
		},
		{
			label: "Last Name",
			variant: "outlined",
			fullWidth: true,
			name: "last_name",
		},
		{
			label: "Email",
			variant: "outlined",
			fullWidth: true,
			name: "email",
		},
		{
			label: "Subject",
			variant: "outlined",
			fullWidth: true,
			name: "subject",
		},
		{
			label: `${options.messageLabel || defaultOptions.messageLabel}`,
			variant: "outlined",
			multiline: true,
			rows: "4",
			fullWidth: true,
			name: "message",
		},
	];
}
